<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions gap-2">
          <button class="btn btn-white" @click="fetchRows" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            Refresh
          </button>
          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="alert alert-info mb-3">
        Imported PAY100 / legacy history rows are for audit and comparison only.
        Live P9, P10, and statutory reports use approved payroll runs in DoneQuick HR.
        Use the Comparative Payroll report to reconcile imported vs calculated totals.
      </div>

      <div class="row mb-3 dq-stat-grid">
        <div class="col-xl-8">
          <div class="card">
            <div class="card-body">
              <div class="row g-2">
                <div class="col-md-3">
                  <label class="form-label">Source</label>
                  <select v-model="filters.source_type" class="form-select">
                    <option value="">All Sources</option>
                    <option v-for="opt in sourceOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">From Month</label>
                  <input v-model="filters.from_month" type="month" class="form-control" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">To Month</label>
                  <input v-model="filters.to_month" type="month" class="form-control" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Search</label>
                  <input
                    v-model="filters.search"
                    type="text"
                    class="form-control"
                    placeholder="Employee or metric"
                    @keyup.enter="resetAndFetch"
                  />
                </div>
                <div class="col-md-3 d-flex align-items-end">
                  <button class="btn btn-primary w-100" @click="resetAndFetch" :disabled="loading">
                    <i class="ti ti-search me-1"></i>Filter
                  </button>
                </div>
                <div class="col-md-3 d-flex align-items-end">
                  <button class="btn btn-light w-100" @click="resetFilters" :disabled="loading">
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-4 d-flex">
          <div class="row flex-fill">
            <div class="col-sm-6 col-xl-12 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <h6 class="mb-1">Archived Rows</h6>
                  <h4>{{ summary.total_rows || 0 }}</h4>
                  <p class="fs-12 text-muted mb-0">
                    {{ summary.months?.earliest || "-" }} to {{ summary.months?.latest || "-" }}
                  </p>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-xl-12 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <h6 class="mb-1">Source Mix</h6>
                  <p class="fs-13 mb-1">
                    Tax History: {{ summary.source_counts?.payroll_history_tax || 0 }}
                  </p>
                  <p class="fs-13 mb-0">
                    P9A Snapshots: {{ summary.source_counts?.p9a_history || 0 }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
          <div>
            <h5 class="mb-1">Imported Payroll History Archive</h5>
            <p class="text-muted mb-0">
              Read-only legacy payroll history imported from external payroll systems.
            </p>
          </div>
        </div>
        <div class="card-body p-0">
          <div v-if="error" class="alert alert-danger m-3 mb-0">{{ error }}</div>

          <div class="custom-datatable-filter table-responsive">
            <a-table
              class="table datatable thead-light"
              :columns="columns"
              :data-source="rows"
              :loading="loading"
              :pagination="false"
              row-key="id"
              size="small"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'source_type'">
                  <span
                    class="badge"
                    :class="
                      record.source_type === 'p9a_history' ? 'bg-soft-warning text-warning' : 'bg-soft-info text-info'
                    "
                  >
                    {{ formatSourceType(record.source_type) }}
                  </span>
                </template>

                <template v-else-if="column.key === 'employee'">
                  <div class="fw-medium">{{ record.employee?.name || "-" }}</div>
                  <div class="text-muted fs-12">{{ record.employee?.employee_id || record.source_employee_code || "-" }}</div>
                </template>

                <template v-else-if="column.key === 'metric_value'">
                  {{ formatAmount(record.metric_value) }}
                </template>

                <template v-else-if="column.key === 'snapshot'">
                  <div class="dq-snapshot-cell">
                    {{ snapshotSummary(record) }}
                  </div>
                </template>
              </template>
            </a-table>
          </div>

          <div class="dq-pagination-footer border-top">
            <p class="text-muted mb-0 dq-pagination-summary">
              Showing {{ meta.from || 0 }} - {{ meta.to || 0 }} of {{ meta.total || 0 }}
            </p>
            <div class="dq-pagination-wrap">
              <a-pagination
                size="small"
                :current="meta.current_page || 1"
                :page-size="meta.per_page || 25"
                :total="meta.total || 0"
                :show-less-items="true"
                show-size-changer
                :page-size-options="['10', '25', '50', '100']"
                @change="handlePageChange"
                @showSizeChange="handlePageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import reportsService from "@/services/reports.service";

const title = "Imported Payroll History";
const text = "Administration";
const text1 = "Imported Payroll History Report";

const loading = ref(false);
const error = ref("");
const rows = ref([]);
const meta = ref({
  current_page: 1,
  per_page: 25,
  total: 0,
  from: 0,
  to: 0,
});
const summary = ref({
  total_rows: 0,
  source_counts: {},
  months: {},
});
const sourceOptions = ref([]);

const filters = reactive({
  source_type: "",
  from_month: "",
  to_month: "",
  search: "",
  page: 1,
  per_page: 25,
});

const columns = computed(() => [
  { title: "Source", dataIndex: "source_type", key: "source_type", width: 160 },
  { title: "Employee", dataIndex: "employee", key: "employee", width: 220 },
  { title: "Payroll Month", dataIndex: "payroll_month", key: "payroll_month", width: 120 },
  { title: "Metric", dataIndex: "metric_label", key: "metric_label", width: 180 },
  { title: "Value", dataIndex: "metric_value", key: "metric_value", width: 130 },
  { title: "Snapshot Details", dataIndex: "payload", key: "snapshot" },
]);

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function formatSourceType(type) {
  return type === "p9a_history" ? "P9A History" : "Payroll History (Tax)";
}

function formatAmount(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function snapshotSummary(record) {
  const payload = record?.payload || {};

  if (record.source_type === "p9a_history") {
    return [
      `Gross ${formatAmount(payload.gross_pay)}`,
      `Chargeable ${formatAmount(payload.chargeable_pay)}`,
      `Tax Deducted ${formatAmount(payload.tax_deducted)}`,
    ].join(" · ");
  }

  return [
    payload.column_number ? `Column ${payload.column_number}` : null,
    payload.tax_not_tax ? `Type ${payload.tax_not_tax}` : null,
    payload.value !== undefined && payload.value !== null ? `Stored ${formatAmount(payload.value)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

async function fetchRows() {
  loading.value = true;
  error.value = "";

  try {
    const { data } = await reportsService.importedPayrollHistory({
      source_type: filters.source_type || undefined,
      from_month: filters.from_month || undefined,
      to_month: filters.to_month || undefined,
      search: filters.search || undefined,
      page: filters.page,
      per_page: filters.per_page,
    });

    rows.value = data?.data || [];
    meta.value = data?.meta || meta.value;
    summary.value = data?.summary || summary.value;
    sourceOptions.value = data?.filter_options?.source_types || [];
  } catch (err) {
    error.value =
      err?.response?.data?.message || "Failed to load imported payroll history.";
  } finally {
    loading.value = false;
  }
}

function handlePageChange(page, pageSize) {
  filters.page = page;
  filters.per_page = pageSize;
  fetchRows();
}

function resetAndFetch() {
  filters.page = 1;
  fetchRows();
}

function resetFilters() {
  filters.source_type = "";
  filters.from_month = "";
  filters.to_month = "";
  filters.search = "";
  filters.page = 1;
  filters.per_page = 25;
  fetchRows();
}

onMounted(() => {
  fetchRows();
});
</script>

<style scoped>
.dq-page-actions {
  align-items: stretch !important;
}

.dq-snapshot-cell {
  white-space: normal;
  min-width: 280px;
}

.dq-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 1rem;
  flex-wrap: wrap;
}

.dq-pagination-summary {
  flex: 0 0 auto;
}

.dq-pagination-wrap {
  flex: 1 1 320px;
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

:deep(.dq-pagination-wrap .ant-pagination) {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px 0;
  margin: 0;
  width: auto;
}

:deep(.dq-pagination-wrap .ant-pagination-item),
:deep(.dq-pagination-wrap .ant-pagination-prev),
:deep(.dq-pagination-wrap .ant-pagination-next),
:deep(.dq-pagination-wrap .ant-pagination-jump-prev),
:deep(.dq-pagination-wrap .ant-pagination-jump-next),
:deep(.dq-pagination-wrap .ant-pagination-options) {
  margin-bottom: 0;
}

@media (max-width: 767.98px) {
  .dq-pagination-footer {
    align-items: stretch;
  }

  .dq-pagination-wrap {
    flex-basis: 100%;
    justify-content: flex-start;
  }

  :deep(.dq-pagination-wrap .ant-pagination) {
    justify-content: flex-start;
  }
}
</style>


